import { formatDashboardMoney } from './procurementDashboardCurrency.js';

const MAX_ID = 2147483647;
const PRICE_SCALE = 1000000n;
const RATE_SCALE = 10000n;
const fallbackCurrencies = ['AED', 'AUD', 'CAD', 'CHF', 'CNY', 'EUR', 'GBP', 'HKD', 'INR', 'NZD', 'QAR', 'SAR', 'SGD', 'USD', 'ZAR'];
let availableCurrencies = fallbackCurrencies;
try { availableCurrencies = Intl.supportedValuesOf?.('currency') || fallbackCurrencies; } catch {}
export const PO_CURRENCIES = Object.freeze(availableCurrencies.filter(code =>
  new Intl.NumberFormat('en', { style: 'currency', currency: code }).resolvedOptions().maximumFractionDigits === 2));
function fail(message, code = 'PO_FINANCIAL_INPUT_INVALID') {
  throw Object.assign(new Error(message), { code });
}
function id(value) {
  if (!['number', 'string'].includes(typeof value) || !/^[1-9][0-9]*$/.test(String(value)) ||
      !Number.isSafeInteger(Number(value)) || Number(value) > MAX_ID) return null;
  return Number(value);
}
function decimal(value, places, message) {
  if (!['number', 'string'].includes(typeof value) || !/^[0-9]{1,20}(?:\.[0-9]{1,30})?$/.test(String(value))) fail(message);
  const normalized = String(value).replace(/^0+(?=\d)/, '')
    .replace(/(\.[0-9]*?)0+$/, '$1').replace(/\.$/, '');
  if ((normalized.split('.')[1] || '').length > places) fail(message);
  return normalized;
}
export function poTaxRate(value) {
  const rate = decimal(value, 4, 'Enter a tax rate from 0 to 100, with up to four decimal places.');
  if (Number(rate) > 100) fail('Enter a tax rate from 0 to 100.');
  return rate;
}
export function poUnitPrice(value) {
  const price = decimal(value, 6, 'Enter a positive quoted unit price with up to six decimal places.');
  if (Number(price) <= 0 || price.split('.')[0].length > 9 ||
      decimal(String(Number(price)), 6, 'The quoted unit price has too many digits.') !== price) {
    fail('Enter a positive quoted unit price with up to nine digits before the decimal point.');
  }
  return price;
}
export function poCurrency(value) {
  if (typeof value !== 'string' || !/^[A-Z]{3}$/.test(value)) fail('Choose the PO transaction currency.');
  return value;
}
export function isPoCurrencySupported(value) {
  try {
    poCurrency(value);
    return PO_CURRENCIES.includes(value);
  } catch { return false; }
}
export function poExpectedScope(user) {
  const scope = { company_id: id(user?.company_id), unit_id: id(user?.unit_id), user_id: id(user?.id) };
  if (Object.values(scope).some(value => value === null)) fail('Select a company and unit before opening this PO form.', 'PO_CONTEXT_CHANGED');
  return scope;
}
export function verifyPoScope(actual, expected) {
  if (!actual || Object.entries(expected).some(([key, value]) => !id(actual[key]) || id(actual[key]) !== id(value))) {
    fail('The selected company, unit or account changed. Reopen this PO form.', 'PO_CONTEXT_CHANGED');
  }
}
export function validatePoFinancialContext(value, expected) {
  verifyPoScope(value?.scope, expected);
  if (value.version !== 1 || value.current_rules?.unit_price_basis !== 'tax_exclusive' ||
      value.current_rules.total_decimal_places !== 2 || value.current_rules.line_tax_rates !== true ||
      !Array.isArray(value.configuration_missing)) fail('The PO currency and tax settings could not be verified.');
  for (const key of ['unit_currency', 'reporting_currency']) if (value[key] !== null) poCurrency(value[key]);
  if (value.default_tax_rate !== null) poTaxRate(value.default_tax_rate);
  if (value.defaults?.transaction_currency !== value.unit_currency || value.defaults?.line_tax_rate !== value.default_tax_rate) {
    fail('The PO defaults could not be verified.');
  }
  if (value.locale !== null) {
    try { if (typeof value.locale !== 'string' || !Intl.getCanonicalLocales(value.locale)[0]) throw new Error(); }
    catch { fail('The PO display settings could not be verified.'); }
  }
  return value;
}
export function poFinancialWritesReady(context) {
  return context?.current_rules?.expected_context_guard === true &&
    context.current_rules.financial_readback === true;
}

export function serializePoLine(item) {
  const quantity = id(item?.quantity);
  if (!quantity) fail('Each quantity must be a positive whole number.');
  const type = item.line_type ?? (item.item_code ? 'catalogue' : 'manual');
  if (!['catalogue', 'manual'].includes(type)) fail('The PO line type could not be verified.');
  const name = String(item.item_name ?? item.description ?? '').trim();
  const code = type === 'catalogue' ? String(item.item_code ?? '').trim().toUpperCase() : '';
  const unitSize = String(item.unit_size ?? '').trim();
  if (!unitSize || (type === 'catalogue' && !code) || (type === 'manual' && !name)) fail('Each PO line needs an item description or code and unit size.');
  return {
    line_type: type, item_code: code, item_name: type === 'manual' ? name : null,
    supplier_item_code: type === 'manual' ? item.supplier_item_code ?? null : null,
    quantity, unit_price: poUnitPrice(item.unit_price), unit_size: unitSize,
    tax_rate: poTaxRate(item.tax_rate),
  };
}
function scaled(raw, places) {
  const [whole, fraction = ''] = raw.split('.');
  return BigInt(whole) * 10n ** BigInt(places) + BigInt(fraction.padEnd(places, '0'));
}
const round = (numerator, denominator) => (numerator + denominator / 2n) / denominator;
function moneyString(minor) {
  const absolute = minor < 0n ? -minor : minor;
  return `${minor < 0n ? '-' : ''}${absolute / 100n}.${String(absolute % 100n).padStart(2, '0')}`;
}
// Matches the current server's numeric aggregation followed by two-place rounding.
export function poFxRate(value) {
  if (typeof value !== 'string') fail('Enter an exchange rate using a decimal point.');
  const rate = decimal(value, 10, 'Exchange rates support up to ten decimal places.');
  if (!/[1-9]/.test(rate) || rate.split('.')[0].length > 10) fail('Enter a positive exchange rate with up to ten digits before the decimal point.');
  return rate;
}
export function previewPoTotals(items, fxRate = null) {
  let net = 0n, tax = 0n;
  const denominator = PRICE_SCALE * RATE_SCALE * 100n;
  for (const source of items) {
    const item = serializePoLine(source);
    const lineNet = BigInt(item.quantity) * scaled(item.unit_price, 6);
    net += lineNet;
    tax += lineNet * scaled(item.tax_rate, 4);
  }
  const fxScale = fxRate === null ? 1n : 10000000000n;
  const fx = fxRate === null ? 1n : scaled(poFxRate(fxRate), 10);
  const netMinor = round(net * 100n * fx, PRICE_SCALE * fxScale);
  const taxMinor = round(tax * 100n * fx, denominator * fxScale);
  const grossMinor = round((net * RATE_SCALE * 100n + tax) * 100n * fx, denominator * fxScale);
  return { net: moneyString(netMinor), tax: moneyString(taxMinor), gross: moneyString(grossMinor),
    rounding: moneyString(grossMinor - netMinor - taxMinor) };
}
export function assertPoMoneyRange(totals) {
  if (['net','tax','gross'].some(key => !/^[0-9]{1,16}\.[0-9]{2}$/.test(totals[key]))) fail('A PO total exceeds the supported amount. Reduce the quantity, price or exchange rate.');
  return totals;
}
export function poManualFxEntry(context, transaction, fields, saved = null) {
  if (context?.current_rules?.manual_fx_entry !== true || context.current_rules.fx_rate_decimal_places !== 10 ||
      context.current_rules.fx_snapshot_version !== true || context.current_rules.currency_context_guard !== true) fail('Exchange-rate entry is awaiting server activation.');
  const unit = poCurrency(context.unit_currency), reporting = poCurrency(context.reporting_currency);
  poCurrency(transaction);
  if (transaction === unit && transaction === reporting) fail('This PO uses one currency and does not need manual exchange rates.');
  const toUnit = transaction === unit ? '1' : poFxRate(fields.rate_to_unit);
  const toReporting = transaction === reporting ? '1' : unit === reporting ? toUnit : poFxRate(fields.rate_to_reporting);
  if (typeof fields.source_reference !== 'string' || !fields.source_reference.trim() ||
      [...fields.source_reference.trim()].length > 180 || /[\u0000-\u001f\u007f]/.test(fields.source_reference)) fail('Enter the exchange-rate source or quote reference, on one line.');
  if (typeof fields.as_of_date !== 'string' || !/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(fields.as_of_date) || fields.as_of_date.startsWith('0000')) fail('Enter the date of the quoted rate.');
  const date = new Date(fields.as_of_date + 'T00:00:00.000Z');
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0,10) !== fields.as_of_date) fail('Enter a valid quoted-rate date.');
  if (saved && !/^[0-9a-f]{64}$/.test(saved.fx_snapshot_version || '')) fail('Reopen the draft to verify its saved exchange-rate version.');
  return { transaction_currency: transaction, unit_currency: unit, reporting_currency: reporting,
    rate_to_unit: toUnit, rate_to_reporting: toReporting, source_reference: fields.source_reference.trim(),
    as_of_date: fields.as_of_date, expected_version: saved?.fx_snapshot_version ?? null };
}
export function formatPoMoney(value, currency, locale) {
  if (!isPoCurrencySupported(currency)) return 'Unavailable';
  return formatDashboardMoney(value, currency, locale || 'en');
}
export function draftPoFinancials(data, expected, expectedId) {
  const header = data?.header;
  if (id(header?.po_id) !== id(expectedId) || header?.status !== 'Draft' || !Array.isArray(data?.items)) {
    fail('The saved draft could not be verified. Reopen it from the PO list.');
  }
  verifyPoScope(header.response_scope, expected);
  const currency = poCurrency(header.transaction_currency);
  poCurrency(header.unit_currency); poCurrency(header.reporting_currency);
  const items = data.items.map(item => ({ ...item,
    description: item.item_name || item.description || item.item_code,
    quantity: String(item.quantity), unit_price: poUnitPrice(item.unit_price),
    tax_rate: item.tax_rate == null ? '' : poTaxRate(item.tax_rate),
    line_type: item.line_type ?? (item.item_code ? 'catalogue' : 'manual'),
  }));
  return { header, currency, items };
}
export function poFxReady(context, transactionCurrency, header) {
  const unit = context?.unit_currency, reporting = context?.reporting_currency;
  if (!transactionCurrency || !unit || !reporting) return false;
  if (transactionCurrency === unit && transactionCurrency === reporting) return true;
  if (!header || header.transaction_currency !== transactionCurrency || header.unit_currency !== unit ||
      header.reporting_currency !== reporting) return false;
  const rate = value => {
    try {
      const raw = decimal(value, 30, 'Invalid recorded exchange rate.');
      return Number(raw) > 0 && Number.isFinite(Number(raw)) ? raw : null;
    } catch { return null; }
  };
  const toUnit = rate(header.fx_rate_to_unit_exact ?? header.fx_rate_to_unit), toReporting = rate(header.fx_rate_to_reporting_exact ?? header.fx_rate_to_reporting);
  return Boolean(toUnit && toReporting && (transactionCurrency !== unit || toUnit === '1') &&
    (transactionCurrency !== reporting || toReporting === '1') && (unit !== reporting || toUnit === toReporting) &&
    typeof header.fx_rate_source === 'string' && header.fx_rate_source.trim() &&
    header.fx_rate_source.trim().toUpperCase() !== 'IDENTITY' &&
    typeof header.fx_rate_timestamp === 'string' && Number.isFinite(Date.parse(header.fx_rate_timestamp)));
}
export function verifyPoFinancialReadback(data, payload, poId, status) {
  try {
    const header = data?.header;
    verifyPoScope(header?.response_scope, payload.expected_context);
    if (id(header?.po_id) !== id(poId) || header.status !== status ||
        header.transaction_currency !== payload.transaction_currency || !Array.isArray(data.items) ||
        data.items.length !== payload.lineItems.length) throw new Error();
    const expected = payload.lineItems.map(serializePoLine);
    const actual = data.items.map(serializePoLine);
    const signature = item => JSON.stringify(item);
    if (expected.map(signature).sort().join('\n') !== actual.map(signature).sort().join('\n')) throw new Error();
    const totals = previewPoTotals(expected);
    for (const key of ['net', 'tax', 'gross']) {
      const raw = header[`transaction_${key}_total_exact`];
      if (typeof raw !== 'string' || decimal(raw, 2, 'Invalid saved PO total.') !== decimal(totals[key], 2, 'Invalid PO preview.')) throw new Error();
    }
    if (payload.expected_currencies) {
      for (const [prefix, rateKey] of [['unit','fx_rate_to_unit'],['reporting','fx_rate_to_reporting']]) {
        if (header[prefix + '_currency'] !== payload.expected_currencies[prefix + '_currency']) throw new Error();
        const raw = header[rateKey + '_exact'];
        if (status === 'Pending' && raw === null) throw new Error();
        const rate = raw === null ? null : poFxRate(raw);
        if (header.transaction_currency === header[prefix + '_currency'] && rate !== '1') throw new Error();
        const converted = rate === null ? null : assertPoMoneyRange(previewPoTotals(expected, rate));
        for (const part of ['net','tax','gross']) {
          const saved = header[prefix + '_' + part + '_total_exact'];
          if (converted ? typeof saved !== 'string' || decimal(saved,2,'Invalid saved conversion.') !== decimal(converted[part],2,'Invalid conversion.') : saved !== null) throw new Error();
        }
      }
      if (!/^[0-9a-f]{64}$/.test(header.fx_snapshot_version || '')) throw new Error();
      if (status === 'Pending' && !poFxReady({unit_currency:header.unit_currency,reporting_currency:header.reporting_currency},header.transaction_currency,header)) throw new Error();
    }
    if (payload.fx_entry) {
      const fx = payload.fx_entry;
      if (poFxRate(header.fx_rate_to_unit_exact) !== fx.rate_to_unit || poFxRate(header.fx_rate_to_reporting_exact) !== fx.rate_to_reporting ||
          header.fx_rate_source !== 'MANUAL:' + fx.source_reference || new Date(header.fx_rate_timestamp).toISOString() !== fx.as_of_date + 'T00:00:00.000Z') throw new Error();
    }
    return data;
  } catch {
    throw Object.assign(new Error('The PO was saved, but its currency or line tax values could not be confirmed. Review the saved PO before continuing.'),
      { code: 'PO_FINANCIAL_READBACK_UNCONFIRMED', reviewRequired: true, poId });
  }
}
